/**
 * U-15 «Никнейм» — по образцу экрана Username в Telegram iOS.
 *
 * BS §11.3: уникальный публичный `@никнейм`, по которому человека находят в глобальном
 * поиске и пишут ему без совпадения. Формат проверяется по мере ввода на клиенте и называет
 * конкретную причину; занятость и резерв спрашиваются у сервера (`GET /me/username/availability`,
 * устаревшие ответы отбрасываются). «Готово» в шапке — единственное действие записи
 * (`PUT /me/username`); удаление — красной строкой с подтверждением (`DELETE /me/username`).
 * Антифрод (BS §20.1): сервер пускает не больше пяти смен в сутки — 429 показывается под полем
 * со временем до следующей попытки.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { UsernameAvailability } from '@shared/api/types'
import { haptic } from '@shared/lib/motion'
import {
  USERNAME_PROBLEM_TEXT,
  displayLink,
  normalizeUsername,
  profileLink,
  usernameProblem,
  usernameRateLimitText,
  usernameRules,
} from '@shared/lib/username'
import { Button, Dialog, Icon, Spinner, cx, useToast } from '@shared/ui'

import { Screen, useGoBack } from '../../layouts/Screen'
import { refreshMe } from '../../boot/bootstrap'
import { useAppStore } from '../../model/app-store'
import styles from './username.module.css'

const CHECK_DEBOUNCE_MS = 350
/** Галочка в шапке успевает показаться до ухода с экрана. */
const SAVED_PAUSE_MS = 420

type Status =
  | { kind: 'idle' }
  | { kind: 'current' }
  | { kind: 'invalid'; text: string }
  | { kind: 'checking' }
  | { kind: 'available'; handle: string }
  | { kind: 'unavailable'; text: string }

export function UsernameScreen(): React.ReactElement {
  const goBack = useGoBack()
  const toast = useToast()
  const inputId = useId()
  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  const rules = useMemo(() => usernameRules(config), [config])

  const current = me?.profile.username ?? null
  const [value, setValue] = useState(current ?? '')
  const [status, setStatus] = useState<Status>({ kind: current ? 'current' : 'idle' })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removing, setRemoving] = useState(false)
  /** Счётчик «тряски» карточки: каждый отказ сервера запускает анимацию заново. */
  const [shake, setShake] = useState(0)
  /** Номер последнего запроса проверки: ответы на устаревший ввод отбрасываются. */
  const requestSeq = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)
  /** Последняя ссылка — текст не исчезает, пока блок сворачивается. */
  const lastLinkRef = useRef<string | null>(null)

  const handle = normalizeUsername(value)

  useEffect(() => {
    requestSeq.current += 1
    const seq = requestSeq.current

    if (!handle) {
      setStatus({ kind: 'idle' })
      return
    }
    if (current !== null && handle === current) {
      setStatus({ kind: 'current' })
      return
    }
    const problem = usernameProblem(handle, rules)
    if (problem) {
      setStatus({ kind: 'invalid', text: USERNAME_PROBLEM_TEXT[problem] })
      return
    }

    setStatus({ kind: 'checking' })
    const timer = setTimeout(() => {
      void api
        .GET('/me/username/availability', { params: { query: { username: handle } as never } })
        .then((result) => unwrap(result) as UsernameAvailability)
        .then((result) => {
          if (seq !== requestSeq.current) return
          if (result.available) {
            setStatus({ kind: 'available', handle: result.username })
          } else {
            setStatus({ kind: 'unavailable', text: USERNAME_PROBLEM_TEXT[result.reason ?? 'invalid_format'] })
            if (result.reason === 'taken') setShake((n) => n + 1)
          }
        })
        .catch(() => {
          // Без ответа сервера подсказка не врёт: занятость проверит сохранение.
          if (seq === requestSeq.current) setStatus({ kind: 'idle' })
        })
    }, CHECK_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [handle, current, rules])

  if (!me) {
    return (
      <Screen title="Никнейм" back>
        {null}
      </Screen>
    )
  }

  const canSave = status.kind === 'available' && !saving && !saved
  const showLink = handle !== '' && (status.kind === 'available' || status.kind === 'current')
  if (showLink) lastLinkRef.current = profileLink(handle, rules)
  const lastLink = lastLinkRef.current
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const fail = (text: string): void => {
    setStatus({ kind: 'unavailable', text })
    setShake((n) => n + 1)
    haptic('medium')
  }

  const save = async (): Promise<void> => {
    setSaving(true)
    try {
      unwrap(await api.PUT('/me/username', { body: { username: handle } as never }))
      await refreshMe()
      setSaved(true)
      haptic('success')
      toast.show('Никнейм сохранён')
      setTimeout(goBack, SAVED_PAUSE_MS)
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'username_taken') {
          fail(USERNAME_PROBLEM_TEXT.taken)
        } else if (error.code === 'rate_limited') {
          fail(usernameRateLimitText(error.retryAfter))
        } else if (error.code === 'validation_failed') {
          const code = error.fieldErrors().get('username')
          fail(
            code === 'not_allowed_word'
              ? USERNAME_PROBLEM_TEXT.reserved
              : code === 'too_short' || code === 'too_long'
                ? USERNAME_PROBLEM_TEXT[code]
                : USERNAME_PROBLEM_TEXT.invalid_format,
          )
        } else {
          toast.show('Не удалось сохранить никнейм', { tone: 'danger' })
        }
      } else {
        toast.show('Нет соединения. Никнейм не сохранён.', { tone: 'danger' })
      }
    } finally {
      setSaving(false)
    }
  }

  const remove = async (): Promise<void> => {
    setRemoving(true)
    try {
      unwrap(await api.DELETE('/me/username'))
      await refreshMe()
      haptic('light')
      toast.show('Никнейм удалён')
      goBack()
    } catch {
      toast.show('Не удалось удалить никнейм', { tone: 'danger' })
    } finally {
      setRemoving(false)
      setRemoveOpen(false)
    }
  }

  const copyLink = (): void => {
    if (!lastLink) return
    void navigator.clipboard
      .writeText(lastLink)
      .then(() => {
        haptic('light')
        toast.show('Ссылка скопирована')
      })
      .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
  }

  const shareLink = (): void => {
    if (!lastLink) return
    void navigator.share({ title: 'Мой профиль в Движе', url: lastLink }).catch(() => undefined)
  }

  const tone: 'ok' | 'current' | 'bad' | 'neutral' =
    status.kind === 'available'
      ? 'ok'
      : status.kind === 'current'
        ? 'current'
        : status.kind === 'invalid' || status.kind === 'unavailable'
          ? 'bad'
          : 'neutral'

  const glyph =
    status.kind === 'checking' ? (
      <Spinner size={16} />
    ) : status.kind === 'available' ? (
      <span key="ok" className={cx(styles['glyph'], styles['glyphOk'])}>
        <Icon name="check" size={13} />
      </span>
    ) : status.kind === 'current' ? (
      <span key="current" className={cx(styles['glyph'], styles['glyphCurrent'])}>
        <Icon name="check" size={13} />
      </span>
    ) : status.kind === 'unavailable' ? (
      <span key="bad" className={cx(styles['glyph'], styles['glyphBad'])}>
        <Icon name="close" size={13} />
      </span>
    ) : null

  const statusText =
    status.kind === 'checking'
      ? 'Проверяем…'
      : status.kind === 'available'
        ? `@${status.handle} свободен`
        : status.kind === 'current'
          ? 'Это ваш текущий никнейм'
          : status.kind === 'invalid' || status.kind === 'unavailable'
            ? status.text
            : ''

  return (
    <Screen
      title="Никнейм"
      back
      narrow
      actions={
        <Button
          variant="ghost"
          className={styles['done']}
          disabled={!canSave}
          loading={saving}
          iconStart={saved ? 'check' : undefined}
          onClick={() => void save()}
        >
          Готово
        </Button>
      }
    >
      <div className={styles['page']}>
        <section className={cx(styles['group'], styles['enter'])} style={{ '--i': 0 } as React.CSSProperties}>
          <h2 className={styles['groupTitle']}>Никнейм</h2>

          <div
            key={shake}
            className={cx(styles['card'], styles['fieldCard'], shake > 0 && styles['fieldCardShake'])}
            data-tone={tone}
          >
            <label className={styles['fieldLabel']} htmlFor={inputId}>
              @
            </label>
            <input
              ref={inputRef}
              id={inputId}
              className={styles['field']}
              value={value}
              maxLength={rules.max + 1}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              inputMode="text"
              enterKeyHint="done"
              placeholder="никнейм"
              aria-label="Никнейм"
              aria-invalid={tone === 'bad' || undefined}
              aria-describedby={`${inputId}-status`}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canSave) void save()
              }}
            />
            {glyph}
            {value ? (
              <button
                type="button"
                className={styles['clear']}
                aria-label="Очистить"
                // Фокус остаётся в поле: клавиатура на телефоне не прячется на каждое нажатие
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => {
                  setValue('')
                  inputRef.current?.focus()
                }}
              >
                <Icon name="close" size={14} />
              </button>
            ) : null}
          </div>

          <div id={`${inputId}-status`} className={styles['statusLine']} role="status" aria-live="polite">
            {statusText ? (
              <span
                key={statusText}
                className={cx(
                  styles['status'],
                  status.kind === 'available' && styles['statusOk'],
                  status.kind === 'current' && styles['statusCurrent'],
                  tone === 'bad' && styles['statusBad'],
                )}
              >
                {statusText}
              </span>
            ) : null}
          </div>

          <p className={styles['note']}>
            По никнейму вас найдут в поиске и напишут напрямую, без совпадения. Телефон и почта не
            раскрываются.
          </p>
          <p className={styles['note']}>
            Буквы <b>a–z</b>, цифры и подчёркивание, от <b>{rules.min}</b> символов.
          </p>

          <div className={cx(styles['reveal'], showLink && styles['revealOpen'])} aria-hidden={!showLink}>
            <div className={styles['revealInner']}>
              <p className={styles['note']}>Эта ссылка открывает ваш профиль:</p>
              <button
                type="button"
                className={styles['link']}
                onClick={copyLink}
                tabIndex={showLink ? 0 : -1}
              >
                {lastLink ? displayLink(lastLink) : ''}
              </button>
              <div className={styles['linkActions']}>
                <Button
                  size="sm"
                  variant="tonal"
                  iconStart="copy"
                  onClick={copyLink}
                  tabIndex={showLink ? 0 : -1}
                >
                  Копировать
                </Button>
                {canShare ? (
                  <Button
                    size="sm"
                    variant="tonal"
                    iconStart="share"
                    onClick={shareLink}
                    tabIndex={showLink ? 0 : -1}
                  >
                    Поделиться
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {current ? (
          <section
            className={cx(styles['group'], styles['enter'])}
            style={{ '--i': 1 } as React.CSSProperties}
          >
            <div className={styles['card']}>
              <button type="button" className={styles['row']} onClick={() => setRemoveOpen(true)}>
                <span className={styles['rowIcon']}>
                  <Icon name="trash" size={16} />
                </span>
                <span className={styles['rowLabel']}>Удалить никнейм</span>
              </button>
            </div>
            <p className={styles['note']}>
              Вас перестанут находить по @{current}, ссылка перестанет работать; начатые диалоги сохранятся.
            </p>
          </section>
        ) : null}
      </div>

      <Dialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        dismissOnScrim={false}
        title="Удалить никнейм?"
        description={`Вас больше нельзя будет найти по @${current ?? ''}, а ссылка на профиль перестанет работать. Уже начатые диалоги сохранятся.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveOpen(false)}>
              Отмена
            </Button>
            <Button variant="danger" loading={removing} onClick={() => void remove()}>
              Удалить
            </Button>
          </>
        }
      />
    </Screen>
  )
}
